import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from './api';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  user_type?: string;
  systemRoles?: string[];
  avatar_url?: string | null;
  twoFactorEnabled?: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string, twoFactorCode?: string) => Promise<{ success: boolean; error?: string; requiresTwoFactor?: boolean }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => ({ success: false }),
  logout: async () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const res = await api.get<{ user: AuthUser }>('/auth/me');
    if (res.success) {
      setUser(res.data.user);
    } else {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email: string, password: string, twoFactorCode?: string) => {
    const res = await api.post<{ user?: AuthUser; requiresTwoFactor?: boolean }>('/auth/login', { email, password, twoFactorCode });
    if (res.success) {
      if (res.data.requiresTwoFactor) {
        return { success: true, requiresTwoFactor: true };
      }
      if (res.data.user) {
        setUser(res.data.user);
      }
      return { success: true };
    }
    return { success: false, error: res.error };
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
